import { createRouter, createWebHistory, type RouteLocationNormalized } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import type { Role } from '../api/types';

declare module 'vue-router' {
  interface RouteMeta {
    public?: boolean;
    roles?: Role[];
  }
}

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/LoginView.vue'),
      meta: { public: true },
    },
    {
      path: '/',
      component: () => import('../components/AppLayout.vue'),
      children: [
        { path: '', redirect: '/files' },
        {
          path: 'files',
          name: 'files',
          component: () => import('../views/FilesView.vue'),
          meta: { roles: ['admin', 'user', 'operator', 'read_only'] },
        },
        {
          path: 'som',
          name: 'som',
          component: () => import('../views/SomBoardView.vue'),
          meta: { roles: ['admin', 'user', 'operator', 'read_only'] },
        },
        {
          path: 'stats',
          name: 'stats',
          component: () => import('../views/StatsView.vue'),
          meta: { roles: ['admin', 'user', 'operator', 'read_only'] },
        },
        {
          path: 'progress',
          name: 'progress',
          component: () => import('../views/ProgressView.vue'),
          meta: { roles: ['admin', 'user', 'operator', 'read_only'] },
        },
        {
          path: 'users',
          name: 'users',
          component: () => import('../views/UsersView.vue'),
          meta: { roles: ['admin', 'operator'] },
        },
        {
          path: 'scripts',
          name: 'scripts',
          component: () => import('../views/ScriptView.vue'),
          meta: { roles: ['admin', 'user', 'operator'] },
        },
        {
          path: 'links',
          name: 'links',
          component: () => import('../views/LinksView.vue'),
          meta: { roles: ['admin'] },
        },
        {
          path: 'logs',
          name: 'logs',
          component: () => import('../views/LogsView.vue'),
          meta: { roles: ['admin', 'user', 'operator', 'read_only'] },
        },
        {
          path: 'security',
          name: 'security',
          component: () => import('../views/SecurityView.vue'),
          meta: { roles: ['admin'] },
        },
        {
          path: 'storage',
          name: 'storage',
          component: () => import('../views/StorageView.vue'),
          meta: { roles: ['admin'] },
        },
      ],
    },
    { path: '/:pathMatch(.*)*', redirect: '/files' },
  ],
});

function intendedPath(to: RouteLocationNormalized): string {
  return typeof to.query.redirect === 'string' && to.query.redirect.startsWith('/')
    ? to.query.redirect
    : '/files';
}

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  if (!auth.initialized) await auth.bootstrap();
  if (to.meta.public) {
    if (to.name === 'login' && auth.isAuthenticated) return intendedPath(to);
    return true;
  }
  if (!auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } };
  }
  const allowed = to.meta.roles;
  if (allowed && auth.role && !allowed.includes(auth.role)) return { name: 'files' };
  return true;
});

export default router;
